import { ActionReducer, INIT } from '@ngrx/store';
import { ScreenState } from './screen-state.reducer';

// The store lives in memory, so the screens' state is also mirrored to sessionStorage and read back
// when the store starts — a reload (F5, stale-chunk recovery) comes back to the same filters and
// page. sessionStorage, not localStorage: it is per tab, like the store itself, so two tabs open on
// the same queue keep their own filters instead of overwriting each other. Sign-out clears it along
// with the rest of sessionStorage.
const STORAGE_KEY = 'screenState';

export function persistScreenState<T extends { screenState: ScreenState }>(reducer: ActionReducer<T>): ActionReducer<T> {
  return (state, action) => {
    const next = reducer(state, action);
    if (action.type === INIT) {
      const saved = readSaved();
      return saved ? { ...next, screenState: { ...next.screenState, ...saved } } : next;
    }
    if (next.screenState !== state?.screenState) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next.screenState));
      } catch {
        /* storage full or blocked — the store still has it until the page unloads */
      }
    }
    return next;
  };
}

function readSaved(): Partial<ScreenState> | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return saved && typeof saved === 'object' ? saved : null;
  } catch {
    return null;
  }
}
